import * as React from 'react';

import MetricsBar from 'components/metrics-bar/MetricsBar';

import { __t, applyTranslations } from 'utils/language';
import { renderPrice } from 'utils/helper/price';
import { insertIf } from 'utils/list';
import {
  DELIVERABLE_TYPE_INSTAGRAM_POST,
  DELIVERABLE_TYPE_INSTAGRAM_REEL,
  DELIVERABLE_TYPE_INSTAGRAM_STORY,
  DELIVERABLE_TYPE_TIKTOK_VIDEO
} from 'utils/deliverables';

import type { BudgetSplitResourceV2 } from 'modules/reporting/types/BudgetSplitResource.type';
import type { MetricsBarItem } from 'types';

import './cca-metrics-summary.scss';

applyTranslations(require('./cca-metrics-summary.i18n.json'));

type Props = {
  splits: Array<BudgetSplitResourceV2>
};

function prepareCreatorCostAttributionMetrics(splits: Array<BudgetSplitResourceV2>): Array<MetricsBarItem> {
  let totalBudget = 0;
  let igPosts = 0;
  let igStories = 0;
  let igReels = 0;
  let ttVideos = 0;

  splits.forEach((split) => {
    totalBudget += split.budgetSplit.totalBudget;

    split.budgetSplit.taskSplits.forEach((task) => {
      if (task.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_POST) {
        igPosts += task.count ?? 0;
      }
      if (task.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_STORY) {
        igStories += task.count ?? 0;
      }
      if (task.deliverableType === DELIVERABLE_TYPE_INSTAGRAM_REEL) {
        igReels += task.count ?? 0;
      }
      if (task.deliverableType === DELIVERABLE_TYPE_TIKTOK_VIDEO) {
        ttVideos += task.count ?? 0;
      }
    });
  });

  return [
    {
      key: 'label',
      value: __t('cca_metrics_summary.main_label'),
      name: null
    },
    {
      key: 'content-creators',
      value: splits.length,
      name: __t('cca_metrics_summary.content_creator_label', { withPlural: splits.length })
    },
    {
      key: 'budget',
      value: renderPrice(totalBudget),
      name: __t('cca_metrics_summary.budget_label')
    },
    ...insertIf(igPosts, {
      key: 'ig-posts',
      value: igPosts,
      name: __t('cca_metrics_summary.ig_posts_label', { withPlural: igPosts })
    }),
    ...insertIf(igStories, {
      key: 'ig-stories',
      value: igStories,
      name: __t('cca_metrics_summary.ig_stories_label', { withPlural: igStories })
    }),
    ...insertIf(igReels, {
      key: 'ig-reels',
      value: igReels,
      name: __t('cca_metrics_summary.ig_reels_label', { withPlural: igReels })
    }),
    ...insertIf(ttVideos, {
      key: 'tt-videos',
      value: ttVideos,
      name: __t('cca_metrics_summary.tt_videos_label', { withPlural: ttVideos })
    })
  ];
}

const CCAMetricsSummary = (
  {
    splits
  }: Props
): JSX.Element => (
  <section className="cca-metrics-summary">
    <MetricsBar
      isMonochrome
      metrics={prepareCreatorCostAttributionMetrics(splits)}
    />
  </section>
);

export default CCAMetricsSummary;
